import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, CardBody, Spinner } from "reactstrap";
import { usePostHog } from "@posthog/react";

import { fetchGameDaily, submitGameAnswer, submitGameDailyShare } from "../hooks/useGame";
import { ASSET_CLASS_LABELS, analogStats, article, displayName, pct, pctOrDash, similarityPct } from "../utils/game";
import GameChart from "./GameChart";
import GameChartToggle, { useGameChartMode } from "./GameChartToggle";
import ShareButton from "./ShareButton";

const PROGRESS_KEY = "dejachart-game-daily-progress";
const STREAK_KEY = "dejachart-game-streak";

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable (private mode, quota) — progress/streak just won't persist
  }
}

/** Whole-day difference between two YYYY-MM-DD strings, computed in UTC so a viewer's
 * local timezone can't shift which calendar day a date string means. */
function daysBetween(a: string, b: string): number {
  const ms = Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`);
  return Math.round(ms / 86400000);
}

function loadStreak(): GameStreak {
  return readJson<GameStreak>(STREAK_KEY) ?? { current: 0, best: 0, lastCompletedDate: null };
}

/** Advance the streak for a just-completed `date`. Consecutive calendar days (vs. the
 * last completed date) increment; a gap resets to 1; completing the SAME date twice
 * (e.g. two tabs, or a re-render) is a no-op so refreshing can't inflate it. */
function advanceStreak(date: string): GameStreak {
  const prev = loadStreak();
  let next: GameStreak;
  if (prev.lastCompletedDate === date) {
    next = prev;
  } else if (prev.lastCompletedDate && daysBetween(prev.lastCompletedDate, date) === 1) {
    next = { current: prev.current + 1, best: prev.best, lastCompletedDate: date };
  } else {
    next = { current: 1, best: prev.best, lastCompletedDate: date };
  }
  next = { ...next, best: Math.max(next.best, next.current) };
  writeJson(STREAK_KEY, next);
  return next;
}

function Dot({ state }: { state: "pending" | "current" | "correct" | "wrong" }) {
  const style: React.CSSProperties = {
    width: 12,
    height: 12,
    borderRadius: "50%",
    display: "inline-block",
    background:
      state === "correct"
        ? "var(--success)"
        : state === "wrong"
          ? "var(--danger)"
          : state === "current"
            ? "var(--primary)"
            : "var(--border-color, #ccc)",
    opacity: state === "pending" ? 0.4 : 1,
  };
  return <span style={style} />;
}

/**
 * GameDailySet — "Call the Chart"'s daily mode: a fixed 5-round set, identical for every
 * player on a given date (GET /api/game/daily). Progress and the day's result persist in
 * localStorage (dejachart-game-daily-progress / dejachart-game-streak) so a reload doesn't
 * lose a run or let a round be re-answered — but the only thing ever TRUSTED as a score is
 * what POST /api/game/share recomputes server-side from the actual guesses.
 *
 * Reuses the same GameChart/reveal pieces as endless mode (Game.tsx) — see this repo's
 * game utils/GameChart for the shared blind-chart + reveal conventions and the "no engine
 * hit rate" rule that governs everything rendered here.
 */
export default function GameDailySet() {
  const posthog = usePostHog();
  const [dailySet, setDailySet] = useState<GameDailySet | null>(null);
  const [progress, setProgress] = useState<GameDailyProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [chartMode, setChartMode] = useGameChartMode();
  const [shareResult, setShareResult] = useState<GameShareResult | null>(null);
  const [streak, setStreak] = useState<GameStreak>(() => loadStreak());
  // The round currently on screen. Deliberately separate from "the first unanswered
  // round" — persisting an answer must not itself advance the screen, or the reveal
  // (ticker/outcome/analog spread) would be replaced by the next chart before the player
  // ever saw it. "Next chart →" is what advances this.
  const [activeRoundId, setActiveRoundId] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    fetchGameDaily()
      .then(set => {
        if (!live) return;
        setDailySet(set);
        const stored = readJson<GameDailyProgress>(PROGRESS_KEY);
        const restored = stored && stored.date === set.date ? stored : { date: set.date, answers: {} };
        setProgress(restored);
        setActiveRoundId(
          set.rounds.find(r => !(r.round_id in restored.answers))?.round_id ?? null,
        );
      })
      .catch(() => live && setError("Couldn't load today's set. Try again in a moment."))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, []);

  const answeredCount = progress ? Object.keys(progress.answers).length : 0;
  const total = dailySet?.rounds.length ?? 0;
  const complete = total > 0 && answeredCount >= total;
  const currentRound = useMemo(
    () => dailySet?.rounds.find(r => r.round_id === activeRoundId) ?? null,
    [dailySet, activeRoundId],
  );
  const currentResult = currentRound ? progress?.answers[currentRound.round_id]?.result : undefined;

  const persistAnswer = useCallback(
    (roundId: number, guessed: GameDirection, result: GameAnswerResult) => {
      setProgress(prev => {
        const base = prev ?? { date: dailySet?.date ?? "", answers: {} };
        const next: GameDailyProgress = {
          date: base.date,
          answers: { ...base.answers, [roundId]: { guess: guessed, result } },
        };
        writeJson(PROGRESS_KEY, next);
        return next;
      });
    },
    [dailySet],
  );

  const advanceToNext = useCallback(() => {
    if (!dailySet) return;
    const answered = progress?.answers ?? {};
    setActiveRoundId(
      dailySet.rounds.find(r => !(r.round_id in answered))?.round_id ?? null,
    );
  }, [dailySet, progress]);

  const answer = async (direction: GameDirection) => {
    if (!currentRound || currentResult || submitting) return;
    setSubmitting(true);
    try {
      const res = await submitGameAnswer(currentRound.round_token, direction);
      persistAnswer(currentRound.round_id, direction, res);
      posthog.capture("game_round_answered", {
        window: currentRound.window,
        asset_class: currentRound.asset_class,
        correct: res.correct,
        engine_correct: res.engine_correct,
        surface: "daily",
      });
    } catch {
      setError("Couldn't score that answer. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Once every round is answered, get the authoritative score + share token. Runs once
  // per completed date (guarded by the effect's own dependency + a share-token check),
  // and also advances the streak — both are idempotent for a re-render/reload on a date
  // already completed (advanceStreak no-ops on a repeat date; the share fetch is cheap
  // to repeat but skipped once we already have a token for this date).
  useEffect(() => {
    if (!complete || !dailySet || !progress) return;
    if (shareResult && shareResult.date === dailySet.date) return;
    const guesses: Record<number, GameDirection> = {};
    for (const [id, a] of Object.entries(progress.answers)) guesses[Number(id)] = a.guess;
    submitGameDailyShare(dailySet.date, guesses)
      .then(res => {
        setShareResult(res);
        setStreak(advanceStreak(dailySet.date));
      })
      .catch(() => {
        // Non-fatal: the player still sees their own tally from `progress`, just without
        // a share card. They can retry by reloading.
      });
  }, [complete, dailySet, progress, shareResult]);

  const assetLabel = currentRound
    ? (ASSET_CLASS_LABELS[currentRound.asset_class] ?? currentRound.asset_class)
    : "";
  const stats = analogStats(currentResult?.analog_curve);
  const name = currentResult ? displayName(currentResult.ticker, currentResult.ticker_name) : null;

  if (loading) {
    return (
      <Card className="mb-3">
        <CardBody className="text-center py-5">
          <Spinner color="primary" />
        </CardBody>
      </Card>
    );
  }

  if (error && !dailySet) {
    return (
      <Card className="mb-3">
        <CardBody className="text-center py-5" style={{ color: "var(--text-muted)" }}>
          <p className="mb-3">{error}</p>
          <Button color="primary" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </CardBody>
      </Card>
    );
  }

  if (!dailySet) return null;

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          {dailySet.rounds.map(r => {
            const a = progress?.answers[r.round_id];
            const state =
              a === undefined
                ? currentRound?.round_id === r.round_id
                  ? "current"
                  : "pending"
                : a.result.correct
                  ? "correct"
                  : "wrong";
            return <Dot key={r.round_id} state={state} />;
          })}
        </div>
        {streak.current > 0 && (
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 14 }}>
            🔥 {streak.current}-day streak{streak.best > streak.current ? ` (best ${streak.best})` : ""}
          </div>
        )}
      </div>

      {currentRound ? (
        <Card className="mb-3">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
                Chart {dailySet.rounds.findIndex(r => r.round_id === currentRound.round_id) + 1} of{" "}
                {total} · {assetLabel}
              </span>
              <GameChartToggle
                mode={chartMode}
                onChange={setChartMode}
                available={Boolean(currentRound.ohlc)}
              />
            </div>
            <div className="mb-2" style={{ fontSize: 13, color: "var(--text-muted)" }}>
              Where does it go over the next{" "}
              {currentRound.horizon === 1 ? "trading day" : `${currentRound.horizon} trading days`}?
            </div>

            <GameChart
              series={currentRound.series}
              ohlc={currentRound.ohlc}
              forward={currentResult?.forward_series}
              forwardOhlc={currentResult?.forward_ohlc}
              realized={currentResult?.realized_direction}
              mode={chartMode}
              forwardPoints={currentRound.horizon}
              onCall={currentResult || submitting ? undefined : d => void answer(d)}
            />

            {!currentResult ? (
              <div className="d-flex justify-content-center gap-3 mt-3">
                <Button color="success" size="lg" disabled={submitting} onClick={() => void answer("up")}>
                  ▲ Up
                </Button>
                <Button color="danger" size="lg" disabled={submitting} onClick={() => void answer("down")}>
                  ▼ Down
                </Button>
              </div>
            ) : (
              <div className="mt-3">
                <p className="mb-2">
                  <strong>{currentResult.ticker}</strong>
                  {name !== null && <span style={{ color: "var(--text-muted)" }}> {name}</span>} on{" "}
                  {currentResult.signal_date} — moved{" "}
                  <strong
                    style={{
                      color: currentResult.realized_return >= 0 ? "var(--success)" : "var(--danger)",
                    }}
                  >
                    {pct(currentResult.realized_return)}
                  </strong>{" "}
                  over the next{" "}
                  {currentResult.horizon === 1 ? "trading day" : `${currentResult.horizon} trading days`}.
                </p>
                {stats && (
                  <p className="mb-2" style={{ fontSize: 13, color: "var(--text-muted)" }}>
                    {stats.count} windows in 20 years of history looked most like this one
                    {typeof currentResult.top_similarity === "number" &&
                      ` — the closest is ${article(similarityPct(currentResult.top_similarity))} ${similarityPct(currentResult.top_similarity)}% shape match`}
                    . What followed them: median {pctOrDash(stats.median)}, middle half{" "}
                    {pctOrDash(stats.p25)} to {pctOrDash(stats.p75)}.
                  </p>
                )}
                <Button color="primary" onClick={advanceToNext}>
                  {answeredCount < total ? "Next chart →" : "See today's result →"}
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      ) : complete ? (
        // Reached only once the player has clicked through the LAST round's own reveal
        // (advanceToNext sets activeRoundId to null when nothing is left unanswered) —
        // `complete` alone flips true the instant the 5th answer is persisted, before
        // that reveal has been shown, so gating on `!currentRound` too is load-bearing.
        <Card className="mb-3">
          <CardBody className="text-center py-4">
            <h2 className="h4 mb-1">
              {shareResult
                ? shareResult.correct
                : dailySet.rounds.filter(r => progress?.answers[r.round_id]?.result.correct).length}
              /{total} today
            </h2>
            <p style={{ color: "var(--text-muted)" }}>
              Come back tomorrow for a new set.{" "}
              {streak.current > 1 ? `${streak.current} days in a row.` : ""}
            </p>
            {shareResult && (
              <ShareButton
                path={`/api/share/game/${shareResult.share_token}`}
                title="I played Call the Chart on DejaChart"
                label="Share your result"
                color="primary"
              />
            )}
          </CardBody>
        </Card>
      ) : null}

      <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
        Today's 5 charts are the same for every player, drawn at random from our signal
        archive — never picked by how they turned out.
      </p>
    </>
  );
}
